import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import useConfirmationDialog from '../../components/dialogs/ConfirmationDialog'
import { NoteEntityRef, NotesApi } from '../../api/NotesApi'
import { useNoteEncryption } from '../../hooks/useNoteEncryption'

const notesApi = new NotesApi()

export const NoteEditorDialog = ({
    entityRef,
    onClose,
    onDeleted,
    onSaved,
    open,
}: {
    entityRef: NoteEntityRef
    onClose: () => void
    onDeleted?: () => void
    onSaved?: () => void
    open: boolean
}) => {
    const { t } = useTranslation()
    const { confirm, ConfirmationDialog } = useConfirmationDialog()
    const { decrypt, encrypt, error: cryptoError, isLoading: isCryptoLoading, isReady } = useNoteEncryption()
    const [content, setContent] = useState('')
    const [initialContent, setInitialContent] = useState('')
    const [hasNote, setHasNote] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const [loadError, setLoadError] = useState<string | null>(null)
    const deleteButtonRef = useRef<HTMLButtonElement>(null)
    const cancelButtonRef = useRef<HTMLButtonElement>(null)

    const focusFirstActionButton = () => {
        if (deleteButtonRef.current && !deleteButtonRef.current.disabled) {
            deleteButtonRef.current.focus()
            return
        }

        cancelButtonRef.current?.focus()
    }

    useEffect(() => {
        if (!open || !isReady) {
            return
        }

        const loadNote = async () => {
            setIsLoading(true)
            setLoadError(null)

            try {
                const note = await notesApi.getNote(entityRef)
                if (!note?.content) {
                    setContent('')
                    setInitialContent('')
                    setHasNote(false)
                    return
                }

                const noteContent = await decrypt(note.content)
                setContent(noteContent)
                setInitialContent(noteContent)
                setHasNote(!!noteContent.trim())
            } catch (err) {
                console.error(err)
                setContent('')
                setInitialContent('')
                setHasNote(false)
                setLoadError(t('pages.notes.errors.couldNotRead'))
            } finally {
                setIsLoading(false)
            }
        }

        void loadNote()
    }, [decrypt, entityRef, isReady, open, t])

    const handleClose = () => {
        setContent(initialContent)
        setLoadError(null)
        onClose()
    }

    const handleSave = async () => {
        if (!isReady) return

        setIsSaving(true)
        try {
            const encryptedContent = await encrypt(content)
            await notesApi.saveNote(entityRef, encryptedContent)
            setInitialContent(content)
            setHasNote(!!content.trim())
            onSaved?.()
            onClose()
        } catch (err) {
            console.error(err)
            setLoadError(t('pages.notes.errors.couldNotSave'))
        } finally {
            setIsSaving(false)
        }
    }

    const handleDelete = async () => {
        const confirmed = await confirm(t('pages.notes.deleteConfirm.message'), {
            title: t('pages.notes.deleteConfirm.title'),
            confirmLabel: t('pages.notes.deleteConfirm.confirmLabel'),
        })
        if (!confirmed) {
            return
        }

        setIsDeleting(true)
        try {
            await notesApi.deleteNote(entityRef)
            setContent('')
            setInitialContent('')
            setHasNote(false)
            onDeleted?.()
            onClose()
        } catch (err) {
            console.error(err)
            setLoadError(t('pages.notes.errors.couldNotDelete'))
        } finally {
            setIsDeleting(false)
        }
    }

    return (
        <>
            <ConfirmationDialog />
            <Dialog fullWidth maxWidth='sm' onClose={handleClose} open={open}>
                <DialogTitle>{t('pages.notes.editor.title')}</DialogTitle>
                <DialogContent>
                    {cryptoError ? <Alert severity='error'>{cryptoError}</Alert> : null}
                    {loadError ? (
                        <Alert severity='warning' sx={{ mt: cryptoError ? 2 : 0 }}>
                            {loadError}
                        </Alert>
                    ) : null}
                    <TextField
                        autoFocus
                        disabled={isLoading || isSaving || isDeleting || !isReady || !!cryptoError || isCryptoLoading}
                        fullWidth
                        minRows={8}
                        multiline
                        onChange={(event) => setContent(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Tab' && !event.shiftKey) {
                                event.preventDefault()
                                focusFirstActionButton()
                            }
                        }}
                        placeholder={t('pages.notes.editor.placeholder')}
                        sx={{ mt: cryptoError || loadError ? 2 : 1 }}
                        value={content}
                    />
                </DialogContent>
                <DialogActions
                    sx={{
                        justifyContent: 'space-between',
                        px: 3,
                        pb: 2,
                        '& .MuiButton-root.Mui-focusVisible': {
                            outline: '2px solid',
                            outlineOffset: 2,
                        },
                        '& .MuiButton-root.MuiButton-colorError.Mui-focusVisible': {
                            outlineColor: 'error.main',
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.error.light}`,
                        },
                        '& .MuiButton-root:not(.MuiButton-colorError).Mui-focusVisible': {
                            outlineColor: 'primary.main',
                            boxShadow: (theme) => `0 0 0 3px ${theme.palette.primary.light}`,
                        },
                    }}
                >
                    <Button
                        ref={deleteButtonRef}
                        color='error'
                        disabled={!hasNote || isLoading || isSaving || isDeleting}
                        onClick={() => void handleDelete()}
                    >
                        {t('site.actions.delete')}
                    </Button>
                    <div>
                        <Button
                            ref={cancelButtonRef}
                            disabled={isSaving || isDeleting}
                            onClick={handleClose}
                            sx={{ mr: 1 }}
                        >
                            {t('site.actions.cancel')}
                        </Button>
                        <Button
                            disabled={
                                isLoading || isSaving || isDeleting || !isReady || !!cryptoError || isCryptoLoading
                            }
                            loading={isSaving}
                            onClick={() => void handleSave()}
                            variant='contained'
                        >
                            {t('site.actions.save')}
                        </Button>
                    </div>
                </DialogActions>
            </Dialog>
        </>
    )
}
